/**
 * Project List Page
 * Displays all projects with search, filters, and summary statistics.
 * Follows the standardized page design pattern.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FolderOpen, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { useProjects } from '../../hooks/useProjects';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { ErrorState, LoadingState, PageBody, PageEmptyState, PageFilterLink, PageGrid, PageHeader, PageToolbar } from '../layout';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ProjectCard } from './ProjectCard';
import { ProjectListRedesign } from './ProjectListRedesign';
export function ProjectList() {
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_projects');
    if (flagsReady && redesignEnabled) {
        return /*#__PURE__*/ _jsx(ProjectListRedesign, {});
    }
    return /*#__PURE__*/ _jsx(ProjectListLegacy, {});
}
function ProjectListLegacy() {
    const [statusFilter, setStatusFilter] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    // Hide breadcrumbs on root page
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    // Fetch all projects to get counts for both statuses
    const { data, isLoading, error, refetch } = useProjects({
        status: 'all'
    });
    // Compute filtered lists and counts
    const { activeProjects, archivedProjects, filteredProjects } = useMemo(()=>{
        const allProjects = data?.projects ?? [];
        const active = allProjects.filter((p)=>p.status === 'active');
        const archived = allProjects.filter((p)=>p.status === 'archived');
        // Get projects for current status filter
        const statusFiltered = statusFilter === 'active' ? active : archived;
        // Apply search filter (case-insensitive on name and description)
        const searchLower = searchQuery.toLowerCase().trim();
        const filtered = searchLower ? statusFiltered.filter((p)=>p.name.toLowerCase().includes(searchLower) || (p.description?.toLowerCase().includes(searchLower) ?? false)) : statusFiltered;
        return {
            activeProjects: active,
            archivedProjects: archived,
            filteredProjects: filtered
        };
    }, [
        data?.projects,
        statusFilter,
        searchQuery
    ]);
    const totalProjects = (data?.projects ?? []).length;
    const hasAnyProjects = totalProjects > 0;
    const openCreateDialog = ()=>setCreateDialogOpen(true);
    // Loading state
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Projects"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Organize related files and chats together"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(LoadingState, {
                        rows: 3
                    })
                })
            ]
        });
    }
    // Error state
    if (error) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Projects"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Organize related files and chats together"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(ErrorState, {
                        message: "Failed to load projects. Please try refreshing the page.",
                        onRetry: refetch
                    })
                })
            ]
        });
    }
    // Empty State - No projects at all
    if (!hasAnyProjects) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Projects"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Organize related files and chats together"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(PageEmptyState, {
                        icon: /*#__PURE__*/ _jsx(FolderOpen, {}),
                        title: "No projects yet",
                        description: "Projects help you organize related files and chats together. Create your first project to get started.",
                        action: {
                            label: 'Create your first project',
                            onClick: openCreateDialog,
                            dataTestid: Testids.project.newProjectButton
                        },
                        size: "lg"
                    })
                }),
                /*#__PURE__*/ _jsx(CreateProjectDialog, {
                    open: createDialogOpen,
                    onOpenChange: setCreateDialogOpen
                })
            ]
        });
    }
    // Has projects
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "Projects"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Organize related files and chats together"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsxs(PageToolbar, {
                        children: [
                            /*#__PURE__*/ _jsx(PageToolbar.Search, {
                                value: searchQuery,
                                onChange: setSearchQuery,
                                placeholder: "Search projects..."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-4",
                                children: [
                                    /*#__PURE__*/ _jsx(PageFilterLink, {
                                        active: statusFilter === 'active',
                                        onClick: ()=>setStatusFilter('active'),
                                        count: activeProjects.length,
                                        children: "Active"
                                    }),
                                    /*#__PURE__*/ _jsx(PageFilterLink, {
                                        active: statusFilter === 'archived',
                                        onClick: ()=>setStatusFilter('archived'),
                                        count: archivedProjects.length,
                                        children: "Archived"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(PageToolbar.Spacer, {}),
                            /*#__PURE__*/ _jsx(PageToolbar.Actions, {
                                children: /*#__PURE__*/ _jsxs(Button, {
                                    onClick: openCreateDialog,
                                    "data-testid": Testids.project.newProjectButton,
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "New Project"
                                    ]
                                })
                            })
                        ]
                    }),
                    filteredProjects.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                        icon: /*#__PURE__*/ _jsx(FolderOpen, {}),
                        title: searchQuery ? 'No matching projects' : statusFilter === 'active' ? 'No active projects' : 'No archived projects',
                        description: searchQuery ? 'Try adjusting your search terms' : statusFilter === 'archived' ? 'Projects you archive will appear here' : 'Create a new project to get started',
                        size: "sm"
                    }) : /*#__PURE__*/ _jsx(PageGrid, {
                        children: filteredProjects.map((project)=>/*#__PURE__*/ _jsx(ProjectCard, {
                                project: project
                            }, project.projectId))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CreateProjectDialog, {
                open: createDialogOpen,
                onOpenChange: setCreateDialogOpen
            })
        ]
    });
}
